import { useState } from 'react'
import type { FieldDefinition } from '@/api/types'
import CustomFieldInput from '@/components/CustomFieldInput'

/**
 * A titled group of custom fields in a two-column grid, shared by the deal and contact detail
 * cards. When more than {@code collapsibleAfter} fields exist, the tail hides behind "Show more"
 * so the details card stays compact.
 */
export default function CustomFieldGroup({
  title,
  fields,
  custom,
  setField,
  collapsibleAfter,
}: {
  title: string
  fields: FieldDefinition[]
  custom: Record<string, unknown>
  setField: (key: string, v: unknown) => void
  collapsibleAfter?: number
}) {
  const [showAll, setShowAll] = useState(false)
  const limit = collapsibleAfter ?? Infinity
  const shown = showAll ? fields : fields.slice(0, limit)
  const hidden = fields.length - shown.length

  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">{title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {shown.map((f) => (
          <div key={f.id}>
            <label className="block text-xs font-medium text-gray-500 mb-1">{f.label}</label>
            <CustomFieldInput def={f} value={custom[f.fieldKey]} onChange={(v) => setField(f.fieldKey, v)} />
          </div>
        ))}
      </div>
      {fields.length > limit && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="text-[11px] text-brand-primary hover:underline mt-2"
        >
          {showAll ? 'Show less' : `Show ${hidden} more`}
        </button>
      )}
    </div>
  )
}
